/**
 * Iter 67ay — Customer-facing cancel reservation dialog.
 *
 * Two-stage UX:
 *   1. Open the dialog → calls `GET /bookings/:id/cancellation-policy` to
 *      determine whether the cancellation falls inside the host's free
 *      cancellation window. Renders a context-aware modal:
 *         - Inside window  → emerald confirmation: "Full refund of €X"
 *         - Outside window → amber warning: "Outside host's N-day window —
 *           non-refundable. Tick the box to confirm."
 *   2. Confirm → POST /bookings/:id/cancel (with `force_outside_window=true`
 *      when applicable) and refreshes the parent list.
 *
 * Uses the same Dialog primitive as `BookingActionsMenu` so the two flows
 * (operator-side + renter-side) feel consistent.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { XCircle, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Money from "@/components/Money";

export default function CustomerCancelDialog({ booking, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [acceptedNoRefund, setAcceptedNoRefund] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPolicy(null);
    setAcceptedNoRefund(false);
    setLoading(true);
    api.get(`/bookings/${booking.id}/cancellation-policy`)
      .then((r) => setPolicy(r.data))
      .catch((err) => {
        console.warn("[CustomerCancelDialog] policy preview failed:", err);
        toast.error(formatApiError(err) || "Couldn't load cancellation policy.");
        setOpen(false);
      })
      .finally(() => setLoading(false));
  }, [open, booking.id]);

  const inside = !!policy?.within_window;
  const windowLabel = policy?.window_label || "48h";
  const refundAmount = Number(booking.amount) || 0;
  // Show a confirm button only after the user accepts the non-refundable
  // disclaimer (when outside the window).
  const canConfirm = !loading && policy && (inside || acceptedNoRefund);

  const onConfirm = async () => {
    setBusy(true);
    try {
      const url = inside
        ? `/bookings/${booking.id}/cancel`
        : `/bookings/${booking.id}/cancel?force_outside_window=true`;
      const r = await api.post(url);
      const refundEligible = !!r.data?.refund_eligible;
      const auto = r.data?.auto_refund;
      let msg;
      if (auto?.refunded) {
        msg = `Cancelled. €${Number(auto.amount).toFixed(2)} refunded to your card — appears in your bank in 5–10 business days.`;
      } else if (refundEligible) {
        msg = "Cancelled. Refund will be processed shortly by the host.";
      } else {
        msg = "Cancelled. This booking was outside the free cancellation window — no refund.";
      }
      toast.success(msg);
      setOpen(false);
      onCancelled?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't cancel the booking.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="mt-1 text-rose-700 hover:text-rose-900 hover:bg-rose-50"
        onClick={() => setOpen(true)}
        data-testid={`customer-cancel-btn-${booking.id}`}
      >
        <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel reservation
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid={`customer-cancel-dialog-${booking.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-rose-600" /> Cancel this reservation?
            </DialogTitle>
            <DialogDescription>
              {booking.car?.make} {booking.car?.model} · {booking.start_date?.slice(0, 10)} → {booking.end_date?.slice(0, 10)}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-8 flex items-center justify-center text-neutral-500" data-testid="customer-cancel-loading">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Checking cancellation policy…
            </div>
          ) : policy && inside ? (
            <div
              className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
              data-testid={`customer-cancel-inside-${booking.id}`}
            >
              <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-emerald-900">
                  Eligible for a full refund
                </div>
                <div className="text-emerald-900/85 mt-1">
                  This booking is inside the host's <strong>free cancellation {windowLabel}</strong> window.
                  You'll be refunded <Money amount={refundAmount} className="font-semibold" /> in full.
                </div>
              </div>
            </div>
          ) : policy ? (
            <div className="space-y-3" data-testid={`customer-cancel-outside-${booking.id}`}>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold text-amber-900">
                    Outside the host's free cancellation window
                  </div>
                  <div className="text-amber-900/85 mt-1">
                    This booking is outside the host's <strong>{windowLabel}</strong> window
                    {policy.hours_until_pickup > 0 && (
                      <> ({Math.round(policy.hours_until_pickup)}h left until pickup)</>
                    )}.
                    Cancelling now is <strong>non-refundable</strong> — you will not receive your <Money amount={refundAmount} className="font-semibold" /> back.
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-neutral-700 cursor-pointer select-none">
                <Checkbox
                  checked={acceptedNoRefund}
                  onCheckedChange={(v) => setAcceptedNoRefund(!!v)}
                  className="mt-0.5"
                  data-testid={`customer-cancel-acknowledge-${booking.id}`}
                />
                <span>I understand this cancellation is non-refundable.</span>
              </label>
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid={`customer-cancel-keep-${booking.id}`}
            >
              Keep my booking
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirm}
              disabled={!canConfirm || busy}
              data-testid={`customer-cancel-confirm-${booking.id}`}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              {inside ? "Cancel & request refund" : "Cancel anyway"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
